import type { ReactNode } from 'react';

import { cn } from '@poseidon/components/cn';
import { Development } from '@poseidon/components/StrokeIcons';

interface Props {
  /** The route's range, e.g. "≥ $1M". */
  label: string;
  /** The contact matched a different route. */
  notTaken?: boolean;
  /** A live route with no steps of its own: matching it ends the sequence. */
  endsHere?: boolean;
  /** The route's own days. */
  children?: ReactNode;
}

/**
 * One lane inside a {@link SequenceTimelineCard}: a label row, then the lane's own day cards nested under it —
 * the way a campaign's "No — not accepted" row holds its steps. A route the contact didn't match folds to its
 * label, faded, and says so.
 */
export function SequenceTimelineRoute({ label, notTaken = false, endsHere = false, children }: Props) {
  return (
    <div
      data-sequence-route={label}
      data-not-taken={notTaken ? '1' : '0'}
      className={cn('flex w-full items-start gap-3 px-3 py-2.5', notTaken && 'opacity-50')}>
      <Development.Dataflow03 aria-hidden className="mt-0.5 size-5 shrink-0 text-lynch-500 dark:text-lynch-400" />
      <div className="flex min-w-0 grow flex-col gap-2">
        <div className="text-sm text-lynch-900 dark:text-lynch-100">
          {label}
          {notTaken && <span className="ml-1.5 text-xs text-lynch-500 dark:text-lynch-400">· not taken</span>}
          {endsHere && <span className="ml-1.5 text-xs text-lynch-500 dark:text-lynch-400">· ends here</span>}
        </div>
        {children}
      </div>
    </div>
  );
}
